/**
 * DrManiQuiz - main orchestrator for the Dr. Mani's Magic quiz.
 *
 * 9 branching questions + an email/zip gate + a 7-section reveal.
 * - Conditional question display (q4_lawn_size only when has_lawn).
 * - Conditional option display (q2 'lawn', q7 'lawn_rings' only when has_lawn).
 * - Side-effects (has_lawn / primary_setup / scale / environment / setup_adds)
 *   are derived from answers each render via computeDerived, so they never go stale.
 * - Transition screen after Q2 and a pre-reveal build-up before the email gate.
 * - Single-selects auto-advance after a 2.5-second acknowledgment (UX v2);
 *   multi-selects keep Continue. The reading counter ticks on every answer.
 * - Submit posts the dmm_quiz_* payload to /api/quiz/submit with brandKey 'drmanismagic'.
 *
 * DMM-native: uses DrManiQuiz.css (dmm-* classes + --dmm-* vars) only — fully
 * decoupled from the USCN CitrusQuiz CSS and its citrus-* / tree-card naming.
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { DMM_QUESTIONS } from '../../data/drManiQuiz/questions';
import { DMM_INTERSTITIALS_AFTER } from '../../data/drManiQuiz/interstitials';
import type { QuizQuestion, QuestionOption, QuizStep } from '../../data/drManiQuiz/types';
import { isQuestionStep } from '../../data/drManiQuiz/types';
import {
  DMM_COUNTER_START,
  getDmmCounterSchedule,
  pickDmmCelebrationLine,
  type DmmCounterStep,
} from '../../data/drManiQuiz/uxCopy';
import {
  resolveDmmQuiz,
  buildDmmKlaviyoPayload,
  type DmmAnswers,
  type DmmDerivedState,
  type DmmResolution,
} from '../../utils/drManiQuizLogic';
import DrManiQuizSlide from './DrManiQuizSlide';
import DrManiEmpathyScreen from './DrManiEmpathyScreen';
import DrManiLoadingScreen from './DrManiLoadingScreen';
import DrManiRevealBuildUp from './DrManiRevealBuildUp';
import DrManiEmailGate from './DrManiEmailGate';
import DrManiResults from './DrManiResults';
import DrManiProgressBar from './DrManiProgressBar';
import DrManiReadingCounter from './DrManiReadingCounter';
import QuizAcknowledgment from '../QuizAcknowledgment';
import { BRAND_CONFIGS } from '../../config/brands';
import './DrManiQuiz.css';

interface DrManiQuizProps {
  apiEndpoint?: string;
  onComplete?: (payload: ReturnType<typeof buildDmmKlaviyoPayload>) => void;
  logoSrc?: string;
}

/** Find the chosen option on a question by value. */
function findOption(question: QuizQuestion | undefined, value?: string): QuestionOption | undefined {
  if (!question || !value) return undefined;
  return question.options.find((o) => o.value === value);
}

/**
 * Evaluate a spec conditional string ("show only if has_lawn" or "... has_lawn == true").
 * Why this matters: the spec encodes display rules as human strings; we parse the
 * variable name and optional equality so the orchestrator can hide/show faithfully.
 */
function evaluateConditional(conditional: string | undefined, derived: DmmDerivedState): boolean {
  if (!conditional) return true;
  const match = conditional.match(/show only if\s+(\w+)(?:\s*==\s*(\S+))?/i);
  if (!match) return true;
  const [, varName, expected] = match;
  const actual = (derived as unknown as Record<string, unknown>)[varName];
  if (expected !== undefined) {
    const exp = expected.replace(/^["']|["']$/g, '');
    return String(actual) === exp;
  }
  return Boolean(actual);
}

/** Derive has_lawn / primary_setup / scale / environment / setup_adds from answers. */
function computeDerived(answers: DmmAnswers): DmmDerivedState {
  const q1 = DMM_QUESTIONS.find((q) => q.id === 'q1_living');
  const q1Option = findOption(q1, answers.dmm_quiz_living);
  const sets = q1Option?.sets || {};
  const hasLawn = Boolean(sets.has_lawn);
  const primarySetup = (sets.primary_setup as string | undefined) ?? undefined;
  const scale = (sets.scale as string | undefined) ?? undefined;

  const q3 = DMM_QUESTIONS.find((q) => q.id === 'q3_setup');
  const setupOptions = (answers.dmm_quiz_setup || [])
    .map((v) => findOption(q3, v))
    .filter((o): o is QuestionOption => Boolean(o));
  const environments = setupOptions.map((o) => o.environment).filter(Boolean);
  const isIndoor = environments.some((e) => e === 'indoor');
  const isOutdoor = environments.some((e) => e === 'outdoor');
  const environment: DmmDerivedState['environment'] =
    isIndoor && isOutdoor ? 'mixed' : isIndoor ? 'indoor' : 'outdoor';
  const setupAdds = Array.from(new Set(setupOptions.flatMap((o) => o.adds || [])));

  return { has_lawn: hasLawn, primary_setup: primarySetup, scale, environment, setup_adds: setupAdds };
}

/** Filter a question's options by their conditional display rules. */
function visibleOptions(question: QuizQuestion, derived: DmmDerivedState): QuestionOption[] {
  return question.options.filter((o) => evaluateConditional(o.conditional, derived));
}

/**
 * Convert stored answers back into the array shape the slide UI uses for selection state.
 * Why this matters: single-select answers are stored as strings for the logic engine,
 * but the option-card UI needs arrays so one rendering path supports both question types.
 */
function selectedValuesForQuestion(question: QuizQuestion, answers: DmmAnswers): string[] {
  const field = question.writes_field as keyof DmmAnswers;
  const value = answers[field];
  return Array.isArray(value) ? value : value ? [value] : [];
}

/** Interleave motivational/loading screens after the questions they follow. */
function buildVisibleSteps(questions: QuizQuestion[]): QuizStep[] {
  const steps: QuizStep[] = [];
  for (const question of questions) {
    steps.push(question);
    const interstitials = DMM_INTERSTITIALS_AFTER[question.id];
    if (interstitials) {
      steps.push(...interstitials);
    }
  }
  return steps;
}

/** Reading-counter display state (UX-5). */
interface CounterState {
  count: number;
  narration: string | null;
  delta: number | null;
  tick: number;
}

const DrManiQuiz: React.FC<DrManiQuizProps> = ({
  apiEndpoint = '/api/quiz/submit',
  onComplete,
  logoSrc,
}) => {
  const [answers, setAnswers] = useState<DmmAnswers>({});
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [zip, setZip] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolution, setResolution] = useState<DmmResolution | null>(null);
  const [ackLine, setAckLine] = useState<string | null>(null);
  const [counter, setCounter] = useState<CounterState>({
    count: DMM_COUNTER_START.count,
    narration: DMM_COUNTER_START.narration,
    delta: null,
    tick: 0,
  });

  // Celebration lines never repeat in a session. The set is seeded with the
  // counter narration lines so a bank line that also appears as narration
  // (Q6's "Your why just cut the field in half. Twice.") never shows twice.
  const usedLinesRef = useRef<Set<string> | null>(null);
  if (usedLinesRef.current === null) {
    usedLinesRef.current = new Set<string>([
      DMM_COUNTER_START.narration,
      ...Object.values(getDmmCounterSchedule(true)).map((s) => s.narration),
    ]);
  }

  const derived = useMemo(() => computeDerived(answers), [answers]);
  const counterSchedule = useMemo(
    () => getDmmCounterSchedule(Boolean(derived.has_lawn)),
    [derived.has_lawn]
  );

  // Visible questions depend on has_lawn (q4 conditional); interstitials are inserted after Q2 and Q9.
  const visibleQuestions = useMemo(
    () => DMM_QUESTIONS.filter((q) => evaluateConditional(q.conditional, derived)),
    [derived]
  );
  const visibleSteps = useMemo(() => buildVisibleSteps(visibleQuestions), [visibleQuestions]);
  const totalProgressSteps = visibleQuestions.length;
  const currentProgressStep = useMemo(
    () => visibleSteps.slice(0, step + 1).filter(isQuestionStep).length,
    [visibleSteps, step]
  );

  // Mobile scroll fix — DMM-native body class so global overflow:hidden does not
  // trap touch scrolling inside the embedded quiz. Mirrors the USCN approach but
  // with its own dmm-quiz-active class (independent CSS hook).
  useEffect(() => {
    document.body.classList.add('dmm-quiz-active');
    document.documentElement.classList.add('dmm-quiz-active');
    return () => {
      document.body.classList.remove('dmm-quiz-active');
      document.documentElement.classList.remove('dmm-quiz-active');
    };
  }, []);

  const atGate = step >= visibleSteps.length;
  const currentStep = !atGate ? visibleSteps[step] : undefined;
  const gateResolution = useMemo(
    () => (atGate ? resolveDmmQuiz(answers, derived) : null),
    [answers, derived, atGate]
  );

  const handleAnswer = useCallback(
    (selected: string[]) => {
      if (!currentStep || !isQuestionStep(currentStep)) return;

      const field = currentStep.writes_field as keyof DmmAnswers;
      const value = currentStep.type === 'single_select' ? selected[0] : selected;
      setAnswers((prev) => ({ ...prev, [field]: value }));
    },
    [currentStep]
  );

  const handleContinue = useCallback(() => {
    setStep((s) => s + 1);
  }, []);

  /**
   * UX-1 + UX-5: called by the slide the moment an answer is submitted.
   * Picks a never-repeated celebration line and ticks the reading counter
   * down while the acknowledgment is still on screen.
   */
  const handleAcknowledge = useCallback(() => {
    if (!currentStep || !isQuestionStep(currentStep)) return '';
    const used = usedLinesRef.current as Set<string>;
    const line = pickDmmCelebrationLine(currentStep.id, used);
    used.add(line);

    const scheduled = counterSchedule[currentStep.id];
    if (scheduled) {
      setCounter((prev) =>
        scheduled.count < prev.count
          ? {
              count: scheduled.count,
              narration: scheduled.narration,
              delta: prev.count - scheduled.count,
              tick: prev.tick + 1,
            }
          : prev
      );
    }
    return line;
  }, [currentStep, counterSchedule]);

  /**
   * Counter value as of arriving at a step: the schedule entry of the last
   * question before it, or the 300 opener. Used to snap (not animate) the
   * chip when the user navigates Back and re-answers.
   */
  const expectedCounterAtStep = useCallback(
    (stepIndex: number): DmmCounterStep => {
      for (let i = Math.min(stepIndex, visibleSteps.length) - 1; i >= 0; i--) {
        const visited = visibleSteps[i];
        if (isQuestionStep(visited)) {
          const scheduled = counterSchedule[visited.id];
          if (scheduled) return scheduled;
        }
      }
      return DMM_COUNTER_START;
    },
    [visibleSteps, counterSchedule]
  );

  // Loading interstitial auto-advances after a short delay, like the USCN quiz.
  useEffect(() => {
    if (!currentStep || currentStep.type !== 'loading' || atGate) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setStep((s) => s + 1);
    }, currentStep.autoAdvanceMs);

    return () => window.clearTimeout(timeout);
  }, [currentStep, atGate]);

  /**
   * Apply every Back action to the latest committed step.
   * Why this matters: functional updates preserve rapid batched clicks instead
   * of calculating each navigation from the same stale render-time value.
   */
  const handleBack = useCallback(() => {
    if (resolution) {
      setResolution(null);
      setStep(visibleSteps.length); // back to the gate
      return;
    }
    setStep((currentStep) => {
      const previousStep = Math.max(0, currentStep - 1);
      // Snap the counter (no tick animation) to where it stood before this step.
      const expected = expectedCounterAtStep(previousStep);
      setCounter((prev) =>
        prev.count === expected.count
          ? prev
          : {
              count: expected.count,
              narration: expected.count === DMM_COUNTER_START.count ? DMM_COUNTER_START.narration : expected.narration,
              delta: null,
              tick: prev.tick,
            }
      );
      return previousStep;
    });
  }, [resolution, visibleSteps.length, expectedCounterAtStep]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const res = resolveDmmQuiz(answers, derived);
      const payload = buildDmmKlaviyoPayload(answers, res, email, zip);

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to submit quiz');

      setResolution(res);
      onComplete?.(payload);
    } catch (err) {
      console.error('DMM quiz submission error:', err);
      setError('Something went wrong sending your reading. Please try again.');
      // Still reveal the reading so the user is never stuck.
      const res = resolveDmmQuiz(answers, derived);
      setResolution(res);
    } finally {
      setIsSubmitting(false);
    }
  }, [answers, derived, email, zip, apiEndpoint, onComplete]);

  // Results reveal.
  if (resolution) {
    return (
      <div className="dmm-quiz dmm-quiz-results">
        <DrManiResults resolution={resolution} onBack={handleBack} />
      </div>
    );
  }

  // Email + zip gate.
  if (atGate) {
    const archetypeName = gateResolution?.archetype.name || 'your garden archetype';
    return (
      <div className="dmm-quiz">
        <div className="dmm-quiz-container">
          <div className="dmm-quiz-content">
            <DrManiEmailGate
              archetypeName={archetypeName}
              email={email}
              zip={zip}
              onEmailChange={setEmail}
              onZipChange={setZip}
              onSubmit={handleSubmit}
              onBack={handleBack}
              isSubmitting={isSubmitting}
              error={error}
            />
          </div>
          <DrManiFooter logoSrc={logoSrc} />
        </div>
      </div>
    );
  }

  if (!currentStep) {
    return <div className="dmm-quiz">Quiz loading…</div>;
  }

  const renderStepContent = () => {
    if (currentStep.type === 'screen') {
      return (
        <DrManiEmpathyScreen
          key={currentStep.id}
          screenId={currentStep.id}
          headline={currentStep.title}
          description={currentStep.description}
          onContinue={handleContinue}
          onBack={step > 0 ? handleBack : undefined}
        />
      );
    }

    if (currentStep.type === 'loading') {
      return (
        <DrManiLoadingScreen
          key={currentStep.id}
          caption={currentStep.caption}
          title={currentStep.title}
          subtitle={currentStep.subtitle}
          description={currentStep.description}
        />
      );
    }

    // UX-6: staged pre-reveal build-up between the last answer and the gate.
    if (currentStep.type === 'buildup') {
      return <DrManiRevealBuildUp key={currentStep.id} onComplete={handleContinue} />;
    }

    const options = visibleOptions(currentStep, derived);
    const selected = selectedValuesForQuestion(currentStep, answers);

    return (
      <DrManiQuizSlide
        key={currentStep.id}
        question={{ ...currentStep, options }}
        selected={selected}
        onAnswer={handleAnswer}
        onContinue={handleContinue}
        onBack={step > 0 ? handleBack : undefined}
        onAcknowledge={handleAcknowledge}
        onAcknowledgmentChange={setAckLine}
      />
    );
  };

  return (
    <div className="dmm-quiz">
      <div className="dmm-quiz-container">
        <div className="dmm-quiz-progress-area">
          <DrManiProgressBar
            current={Math.min(currentProgressStep, totalProgressSteps)}
            total={totalProgressSteps}
            bartype="full"
          />
          {/* Why this matters: an empty optional line still needs a visible
              kicker/progress card during the 2.5-second interaction lock. */}
          {ackLine !== null && (
            <QuizAcknowledgment line={ackLine} kicker="Your reading just changed" />
          )}
          {/* UX-5: the reading counter remains in the same top progress group. */}
          <DrManiReadingCounter
            count={counter.count}
            narration={counter.narration}
            delta={counter.delta}
            tick={counter.tick}
          />
        </div>
        <div className="dmm-quiz-content">{renderStepContent()}</div>
        <DrManiFooter logoSrc={logoSrc} />
      </div>
    </div>
  );
};

/** Logo mark + "Powered by Dr. Mani's Magic" footer line. */
const DrManiFooter: React.FC<{ logoSrc?: string | undefined }> = ({ logoSrc }) => {
  const src = logoSrc || BRAND_CONFIGS.drmanismagic.logoSrc || '';
  return (
    <div className="dmm-quiz-footer">
      {src ? <img src={src} alt="" aria-hidden="true" /> : null}
      <span>Powered by Dr. Mani's Magic</span>
    </div>
  );
};

export default DrManiQuiz;
